/**
 * TopNav (component-inventory.md §2) — the top bar: the mobile menu button, the desktop
 * brand lockup (icon + wordmark), the sidebar collapse toggle and the back/forward history
 * controls to its right (PO ruling 2026-10-08 added the arrows + wordmark; PO ruling
 * 2026-10-05 placed the collapse toggle), the PWA install button (§7), and the user menu
 * (theme, security settings, sign out). The firm switcher and "New case" moved to the top
 * of the sidebar.
 *
 * The firm logo is NOT shown in the shell (PO ruling 2026-10-05: the ARGUS icon is enough; the
 * firm's logo belongs on its generated reports and exports). The principal still carries
 * `firm.logo_url` for the settings preview.
 *
 * Brand lockup (PO 2026-10-08): the 28px icon plus the wordmark PNG, a 4px gap
 * (`--space-1`, `.argus-topnav__brand`'s own `gap`) so they read as one mark. The wordmark
 * asset is ~95% ink to its PNG canvas height (little internal padding); the icon's triangle
 * glyph only fills ~79% of its 128px source canvas (measured via sharp). Matching the
 * wordmark's CAP height — not its PNG box — to the icon's glyph height (28 * 0.79 ≈ 22px)
 * means rendering the wordmark at 23px tall (23 * ~0.95 ≈ 22px of ink); width follows the
 * asset's own aspect ratio via `height:23px; width:auto` in primitives.css (≈65–66px).
 * Hidden below 900px only (`.argus-topnav__hide-mobile`, keeps room for the hamburger + the
 * history arrows); it stays visible when the sidebar collapses (PO 2026-10-26).
 */
import { Link, useRouter, type RouterHistory } from '@tanstack/react-router';
import { useEffect, useState, type JSX } from 'react';

import { Linear } from '../ui/icons.ts';

// Outline (Solar Linear) renditions for the shell controls (PO 2026-10-26).
const { ChevronLeft: AltArrowLeftIcon, ChevronRight: AltArrowRightIcon, Menu: MenuIcon } = Linear;
const { PanelLeftClose, PanelLeftOpen } = Linear;

import { useNav } from '../../store/nav-store.ts';
import { InstallButton } from '../pwa/pwa-controls.tsx';
import { Button } from '../ui/button.tsx';
import { UserMenu } from './user-menu.tsx';

/**
 * Router-history back/forward. Subscribes to the TanStack history object — the real
 * browser history in prod, the in-memory history the test harness renders against — so
 * `canGoBack` stays live across navigations instead of going stale after the first render.
 *
 * `canGoBack` isn't on today's `RouterHistory` type (`@tanstack/history` 1.81.9 has no such
 * method); the optional call guards a future version that adds one. The
 * `history.length <= 1` fallback is what actually runs today, and — unlike
 * `window.history.length` — it reads the router's OWN history (real or memory), so it is
 * correct in a memory-history test render and never touches `window`.
 */
function useHistoryNav(): { back: () => void; forward: () => void; canGoBack: boolean } {
  const router = useRouter();
  const [, bump] = useState(0);
  useEffect(() => router.history.subscribe(() => bump((n) => n + 1)), [router]);
  const history = router.history as RouterHistory & { canGoBack?: () => boolean };
  const canGoBack = history.canGoBack?.() ?? history.length > 1;
  return {
    back: () => router.history.back(),
    forward: () => router.history.forward(),
    canGoBack,
  };
}

export function TopNav(): JSX.Element {
  const collapsed = useNav((s) => s.sidebarCollapsed);
  const toggle = useNav((s) => s.toggleSidebar);
  const mobileOpen = useNav((s) => s.mobileOpen);
  const setMobileOpen = useNav((s) => s.setMobileOpen);
  const { back, forward, canGoBack } = useHistoryNav();

  return (
    <header className="argus-topnav">
      <Button
        variant="ghost"
        iconOnly
        className="argus-topnav__menu"
        aria-label="Open menu"
        aria-controls="primary-nav"
        aria-expanded={mobileOpen}
        onClick={() => setMobileOpen(!mobileOpen)}
        icon={<MenuIcon size={20} aria-hidden="true" />}
      />
      <Link to="/dashboard" className="argus-topnav__brand" aria-label="ARGUS home">
        {/* The ARGUS icon, decorative — the wordmark below carries the one `alt="ARGUS"`
            for the lockup, and the link's own aria-label already names the destination.
            Two renditions: the navy-and-blue one on light surfaces, the original
            white-and-blue one on dark. */}
        <img
          className="argus-brand-icon argus-brand-icon--light"
          src="/icons/argus-mark-light.png"
          alt=""
          width={28}
          height={28}
        />
        <img
          className="argus-brand-icon argus-brand-icon--dark"
          src="/icons/argus-mark-dark.png"
          alt=""
          width={28}
          height={28}
        />
        {/* Wordmark (PO 2026-10-08; stays when the sidebar collapses, PO 2026-10-26): hidden
            only below 900px, where the hamburger + history arrows need the room. The wrap
            (not the images) carries `argus-topnav__hide-mobile`. */}
        <span className="argus-brand-wordmark-wrap argus-topnav__hide-mobile">
          <img
            className="argus-brand-wordmark argus-brand-wordmark--light"
            src="/brand/argus-wordmark-light.png"
            alt="ARGUS"
            width={623}
            height={220}
          />
          <img
            className="argus-brand-wordmark argus-brand-wordmark--dark"
            src="/brand/argus-wordmark-dark.png"
            alt="ARGUS"
            width={623}
            height={218}
          />
        </span>
      </Link>
      <Button
        variant="ghost"
        iconOnly
        className="argus-topnav__toggle"
        aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        aria-expanded={!collapsed}
        aria-controls="primary-nav"
        onClick={toggle}
        icon={
          collapsed ? (
            <PanelLeftOpen size={18} aria-hidden="true" className="argus-topnav__toggle-icon" />
          ) : (
            <PanelLeftClose size={18} aria-hidden="true" className="argus-topnav__toggle-icon" />
          )
        }
      />
      {/* Back/forward (PO 2026-10-08), immediately right of the toggle, same primitive and
          size. Always visible, even below 900px where the toggle itself hides — not given
          `argus-topnav__hide-mobile`. Forward has no "can't go forward" signal in
          `@tanstack/history`, so only Back is gated (useHistoryNav above). */}
      <Button
        variant="ghost"
        iconOnly
        className="argus-topnav__nav-back"
        aria-label="Go back"
        disabled={!canGoBack}
        onClick={back}
        icon={<AltArrowLeftIcon size={18} aria-hidden="true" />}
      />
      <Button
        variant="ghost"
        iconOnly
        className="argus-topnav__nav-forward"
        aria-label="Go forward"
        onClick={forward}
        icon={<AltArrowRightIcon size={18} aria-hidden="true" />}
      />
      <span className="argus-topnav__spacer" />
      <div className="argus-topnav__actions">
        <InstallButton />
        <UserMenu />
      </div>
    </header>
  );
}
